/*
 * Description: Application Footer Web Component
 * Purpose: Displays a structured application footer with brand, navigation, and social sections
 * Author: Nuvemx
 * Created: 2026
 *
 * Features:
 * - Three-column layout: brand, navigation, social/copyright
 * - Logo and brand description
 * - Navigation links via slot
 * - Social icons via slot
 * - Auto-generated copyright year
 *
 * Usage:
 * <application-footer
 *   logo="logo.png"
 *   brand="MyApp"
 *   brand-description="A great application"
 *   copyright="MyApp Inc."
 * >
 *   <a href="/about">About</a>
 *   <a href="/contact">Contact</a>
 *   <a slot="social" href="https://github.com/"><i class="bx bxl-github"></i></a>
 * </application-footer>
 *
 * Attributes:
 * - logo: String - Logo image URL
 * - brand: String - Brand name
 * - brand-description: String - Short brand description
 * - copyright: String - Copyright holder name
 * - year: String - Copyright year (default: current year)
 *
 */

import './application_footer.css';

class application_footer extends HTMLElement {
    connectedCallback() {
        if (this.dataset.rendered) return;

        const logo = this.getAttribute('logo') || '';
        const brand = this.getAttribute('brand') || '';
        const brandDescription = this.getAttribute('brand-description') || '';
        const copyright = this.getAttribute('copyright') || '';
        const year = this.getAttribute('year') || new Date().getFullYear().toString();

        const navLinks = this.querySelectorAll('.footer-nav a, a[href]:not([slot="social"])');
        const navHtml = Array.from(navLinks).map(link =>
            `<li class="mb-1"><a href="${link.getAttribute('href') || '#'}" class="text-decoration-none small">${link.textContent}</a></li>`
        ).join('');

        const socialLinks = this.querySelectorAll('a[slot="social"]');
        const socialHtml = Array.from(socialLinks).map(link =>
            `<a href="${link.getAttribute('href') || '#'}" title="${link.getAttribute('title') || ''}">${link.innerHTML}</a>`
        ).join('');

        this.innerHTML = `
            <footer class="app-footer py-4 mt-auto">
                <div class="container-fluid">
                    <div class="row">
                        <div class="col-md-4 mb-3 mb-md-0">
                            <div class="footer-brand">
                                ${logo ? `<img src="${logo}" alt="${brand}" class="mb-2">` : ''}
                                ${brand ? `<h6 class="mb-1">${brand}</h6>` : ''}
                                ${brandDescription ? `<p class="small text-muted mb-0">${brandDescription}</p>` : ''}
                            </div>
                        </div>
                        <div class="col-md-4 text-md-center mb-3 mb-md-0">
                            <h6 class="mb-3">Links</h6>
                            <ul class="list-unstyled mb-0">
                                ${navHtml || '<li class="text-muted small">No links provided</li>'}
                            </ul>
                        </div>
                        <div class="col-md-4 text-md-end">
                            <div class="footer-social mb-2">
                                ${socialHtml}
                            </div>
                            ${copyright ? `<p class="small text-muted mb-0">&copy; ${year} ${copyright}</p>` : ''}
                        </div>
                    </div>
                </div>
            </footer>`;

        this.dataset.rendered = 'true';
    }
}

export { application_footer };
